import { ApplicationState } from 'app/types-store';
import { ActionDispatching, ComplexValue } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { FieldItem, SelectItem, DomainNode } from '../../types';
import * as actions from './action-types';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { selectAggregateFieldState } from 'app/area/reports/selectors';
import { selectAggregationForSelectedReport, selectWorkingDefinition } from 'app/area/favourite/selectors';
import { actionCreators as favouriteActionCreatorsFactory } from 'app/area/favourite/actions';
import { codeItemToStoreValue, storeValueToCodeItem } from 'app/infrastructure/data/codes';
import { getCaseTypeIds } from 'app/area/reports/caseTypeIds';

export const domainNodeToComplexType = (dn: DomainNode): FieldItem => {
  return {
    value: dn.domain,
    description: dn.label,
    descendants: !!dn.children ? dn.children.map(domainNodeToComplexType) : undefined
  };
};

const addToDefaultFields = (fieldItems: FieldItem[], field: FieldItem) => {
  return !fieldItems.some(f => f.value === field.value);
};

// eslint:disable-next-line: max-func-body-length
export const actionCreators = (api: ReportCommonApi, instanceKey: string) => {

  const {
    setWorkingDefinitionAggregation
  } = favouriteActionCreatorsFactory(instanceKey);

  return {
    clearState: (): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(new actions.ClearAggregationState(instanceKey));
      },
    setAggregateField: (
      field: FieldItem, 
      selectedInitialFunction: SelectItem, 
      selectedInitialUomSet: SelectItem,
      fieldsToClearOnChange: { target?: boolean; rollingAverage?: boolean; selections?: boolean }
    ): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (!field) {
          dispatch(setWorkingDefinitionAggregation(undefined, undefined, undefined));
          return;
        }

        const state = selectAggregateFieldState(instanceKey)(getState());

        if (addToDefaultFields(state.defaultAggregateFields, field)) {
          dispatch(new actions.UpdateAggregateDefaultFields(instanceKey, [...state.defaultAggregateFields, field]));
        }

        await dispatch(actionCreators(api, instanceKey).loadAggregateFieldOptions(field.value));

        const {
          uomSetOptions,
          aggregateFunctionOptions
        } = selectAggregateFieldState(instanceKey)(getState());

        dispatch(setWorkingDefinitionAggregation(
          storeValueToCodeItem(field),
          selectedInitialFunction?.value 
            ? storeValueToCodeItem(selectedInitialFunction) 
            : storeValueToCodeItem(aggregateFunctionOptions?.[0]),
          selectedInitialUomSet?.value 
            ? storeValueToCodeItem(selectedInitialUomSet)
            : storeValueToCodeItem(uomSetOptions?.[0]),
          fieldsToClearOnChange
        ));
      }),
    loadAggregateFieldOptions: (
      fieldId: number
    ): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        const [codetables, uomSetOptions] = await Promise.all([
          api.loadAggregateFunctionOptions(fieldId), 
          api.loadUomSetOptions(fieldId)
        ]);

        dispatch(new actions.SetOptions(
          instanceKey, 
          codetables?.data?.rootCodes?.map(codeItemToStoreValue), 
          uomSetOptions?.data?.rootCodes?.map(codeItemToStoreValue)
        ));
      }),
    setAggregateFunction: (
      aggregateFunction: ComplexValue<any>, 
      uomSet: ComplexValue<any>,
      fieldsToClearOnChange: { target?: boolean; rollingAverage?: boolean; selections?: boolean }
    ): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        const {
          aggregationDomain
        } = selectAggregationForSelectedReport(instanceKey)(getState());
        
        const aggregationType = aggregateFunction?.value ? storeValueToCodeItem(aggregateFunction) : undefined;
        const uomSetDomain = uomSet?.value ? storeValueToCodeItem(uomSet) : undefined;
        
        dispatch(setWorkingDefinitionAggregation(
          aggregationDomain, 
          aggregationType, 
          uomSetDomain,
          fieldsToClearOnChange
        ));
      },
    loadAggregateDefaultFields: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        dispatch(new actions.RequestAggregateDefaultFields(instanceKey));
        const codetables = await api.loadAggregateDefaultFields();
        const options = codetables.data.map(domainNodeToComplexType);

        const aggregationDomain = codeItemToStoreValue(
          selectAggregationForSelectedReport(instanceKey)(getState()).aggregationDomain
        );

        dispatch(new actions.ResponseAggregateDefaultFields(instanceKey, [
          ...(options || []),
          ...(
            !options?.some(o => o?.value === aggregationDomain?.value) && aggregationDomain ? 
              [aggregationDomain] : 
              []
          )
        ]));
      }),
    loadAggregateFields: (query?: string): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: ()=>ApplicationState) => {
        const workingDefinition = selectWorkingDefinition(instanceKey)(getState());
        const codetables = await api.loadAggregateFields(query, getCaseTypeIds(workingDefinition));
        const options = codetables.data.map(domainNodeToComplexType);
        dispatch(new actions.ResponseAggregateFields(instanceKey, options));
      }),
    updateDefaultAggregateFields: (fieldItems: FieldItem[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.UpdateAggregateDefaultFields(instanceKey, fieldItems));
      })
  };
};
